import { useRef, useState } from "react";
import { CheckCircle2, ImagePlus, Loader2, Send, X } from "lucide-react";
import { api, EMAIL_RE, errMsg, fieldErrs } from "@/lib/api";

const MB = 1024 * 1024;
const PHOTO_EXT = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
const blank = { name: "", phone: "", email: "", best_way: "Call", vehicle: "", service: "", message: "", website: "" };
const REQUIRED = "This field is required";
let seq = 0;

const photoOk = (f) => PHOTO_EXT.includes(f.name.split(".").pop().toLowerCase()) && f.size > 0 && f.size <= 15 * MB;
const validPhone = (p) => {
  const d = p.replace(/\D/g, "");
  return d.length === 10 || (d.length === 11 && d[0] === "1");
};

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = REQUIRED;
  if (!f.phone.trim()) e.phone = REQUIRED;
  else if (!validPhone(f.phone)) e.phone = "Enter a valid phone number";
  if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email";
  else if (f.best_way === "Email" && !f.email.trim()) e.email = "Add your email so we can reach you";
  if (!f.message.trim()) e.message = REQUIRED;
  return e;
};

const inputCls = "mt-1.5 h-12 w-full rounded-md border border-zinc-700 bg-[#121214] px-3 text-base text-[#FAF7F2] placeholder:text-zinc-500 focus:border-[#FFD23F] focus:outline-none";

const Field = ({ label, error, id, optional, children }) => (
  <div>
    <label htmlFor={id} className="text-sm font-medium text-zinc-200">
      {label} {optional && <span className="font-normal text-zinc-500">(optional)</span>}
    </label>
    {children}
    {error && <p className="mt-1.5 text-sm font-medium text-[#ff6b5e]" data-testid={`${id}-error`} role="alert">{error}</p>}
  </div>
);

const PhotoChip = ({ p, onRemove }) => (
  <li className="relative h-20 w-20 overflow-hidden rounded-md border border-zinc-700 bg-black" data-testid="message-photo-chip">
    <img src={p.preview} alt={p.file.name} className="h-full w-full object-cover" />
    {p.status === "uploading" && (
      <span className="absolute inset-0 grid place-items-center bg-black/60 text-xs text-[#FFD23F]"><Loader2 className="h-5 w-5 animate-spin" /></span>
    )}
    <button type="button" onClick={onRemove} className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-black/80 text-white" aria-label={`Remove ${p.file.name}`} data-testid="message-photo-remove-button">
      <X className="h-4 w-4" />
    </button>
  </li>
);

export const MessageForm = ({ services, primaryPhone }) => {
  const [f, setF] = useState(blank);
  const [errors, setErrors] = useState({});
  const [photos, setPhotos] = useState([]);
  const [photoErrors, setPhotoErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState("");
  const [submitError, setSubmitError] = useState("");
  const input = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const upload = async (item) => {
    const form = new FormData();
    form.append("file", item.file);
    try {
      const { data } = await api.post("/public/message-photos", form);
      setPhotos((cur) => cur.map((p) => (p.key === item.key ? { ...p, status: "done", id: data.photo_id } : p)));
    } catch (e) {
      setPhotos((cur) => cur.filter((p) => p.key !== item.key));
      setPhotoErrors((cur) => [...cur, e.response?.status === 400 ? errMsg(e) : `${item.file.name}: upload failed — please try again`]);
    }
  };

  const attach = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const bad = files.filter((x) => !photoOk(x));
    const good = files.filter(photoOk);
    const errs = bad.map((x) => `${x.name}: please attach a JPG, PNG, WEBP or HEIC photo up to 15 MB`);
    const room = 5 - photos.length;
    if (good.length > room) errs.push("You can attach up to 5 photos");
    setPhotoErrors(errs);
    const fresh = good.slice(0, Math.max(0, room)).map((file) => ({ key: `p${++seq}`, file, status: "uploading", preview: URL.createObjectURL(file) }));
    setPhotos((cur) => [...cur, ...fresh]);
    fresh.forEach(upload);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setThanks("");
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/messages", { ...f, photo_ids: photos.filter((p) => p.id).map((p) => p.id) });
      setThanks(data.message);
      setF(blank);
      setPhotos([]);
      setPhotoErrors([]);
    } catch (err) {
      const st = err.response?.status;
      if (st === 400) setErrors(fieldErrs(err));
      else if (st === 429) setSubmitError(errMsg(err));
      else setSubmitError(`Something went wrong — please call us at ${primaryPhone}`);
    } finally {
      setBusy(false);
    }
  };

  const uploading = photos.some((p) => p.status === "uploading");
  return (
    <div id="message" className="section-anchor rounded-lg border border-zinc-800 bg-[#1A1A1E] p-5 sm:p-8" data-testid="message-us-section">
      <p className="eyebrow">We'll get back to you</p>
      <h3 className="font-display mt-1 text-4xl text-[#FAF7F2] sm:text-5xl">Message Us</h3>
      <p className="mt-2 text-zinc-400">Tell us about your vehicle and we'll call, text or email you back.</p>
      {thanks && (
        <p className="mt-5 flex items-start gap-2 rounded-md border border-[#7ee08a]/40 bg-[#7ee08a]/10 p-4 text-base text-[#b9f5c0]" data-testid="message-thanks" role="status">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> {thanks}
        </p>
      )}
      <form onSubmit={submit} noValidate className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="message-form">
        <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 opacity-0" data-testid="message-honeypot-input" />
        <Field label="Name" id="message-name" error={errors.name}>
          <input id="message-name" value={f.name} onChange={set("name")} autoComplete="name" className={inputCls} data-testid="message-name-input" />
        </Field>
        <Field label="Phone" id="message-phone" error={errors.phone}>
          <input id="message-phone" type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" placeholder="719-555-0123" className={inputCls} data-testid="message-phone-input" />
        </Field>
        <Field label="Email" id="message-email" optional={f.best_way !== "Email"} error={errors.email}>
          <input id="message-email" type="email" inputMode="email" value={f.email} onChange={set("email")} autoComplete="email" className={inputCls} data-testid="message-email-input" />
        </Field>
        <div>
          <span className="text-sm font-medium text-zinc-200">Best way to reach you</span>
          <div className="mt-1.5 flex gap-2" role="radiogroup">
            {["Call", "Text", "Email"].map((w) => (
              <button key={w} type="button" role="radio" aria-checked={f.best_way === w} onClick={() => set("best_way")(w)}
                className={`h-12 flex-1 rounded-md border text-base font-medium transition-colors ${f.best_way === w ? "border-[#FFD23F] bg-[#FFD23F] text-[#121214]" : "border-zinc-700 text-zinc-200 hover:bg-zinc-800"}`}
                data-testid={`message-best-way-${w.toLowerCase()}`}>
                {w}
              </button>
            ))}
          </div>
        </div>
        <Field label="Vehicle" id="message-vehicle" optional>
          <input id="message-vehicle" value={f.vehicle} onChange={set("vehicle")} placeholder="e.g. 2018 Ford F-150" className={inputCls} data-testid="message-vehicle-input" />
        </Field>
        <Field label="What do you need?" id="message-service" optional>
          <select id="message-service" value={f.service} onChange={set("service")} className={inputCls} data-testid="message-service-select">
            <option value="">Choose one</option>
            {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
            <option value="Other">Other</option>
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Message" id="message-message" error={errors.message}>
            <textarea id="message-message" rows={5} maxLength={2000} value={f.message} onChange={set("message")} className={`${inputCls} h-auto py-3`} data-testid="message-message-input" />
          </Field>
          <p className="mt-1 text-right text-xs text-zinc-500">{f.message.length}/2000</p>
        </div>
        <div className="sm:col-span-2">
          <span className="text-sm font-medium text-zinc-200">Photos of the damage <span className="font-normal text-zinc-500">(optional, up to 5)</span></span>
          <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={attach} data-testid="message-photo-input" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ul className="flex flex-wrap gap-2">
              {photos.map((p) => <PhotoChip key={p.key} p={p} onRemove={() => setPhotos((cur) => cur.filter((x) => x.key !== p.key))} />)}
            </ul>
            {photos.length < 5 && (
              <button type="button" onClick={() => input.current?.click()} className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-zinc-600 text-xs text-zinc-300 transition-colors hover:border-[#FFD23F] hover:text-[#FFD23F]" data-testid="message-add-photo-button">
                <ImagePlus className="h-6 w-6" /> Add
              </button>
            )}
          </div>
          {[...photoErrors, ...(errors.photos ? [errors.photos] : [])].map((m, i) => (
            <p key={i} className="mt-1.5 text-sm font-medium text-[#ff6b5e]" data-testid="message-photo-error" role="alert">{m}</p>
          ))}
        </div>
        <div className="sm:col-span-2">
          {submitError && <p className="mb-3 text-base font-medium text-[#ff6b5e]" data-testid="message-submit-error" role="alert">{submitError}</p>}
          <button type="submit" disabled={busy || uploading} className="btn-gold h-14 w-full text-lg disabled:opacity-60 sm:w-auto" data-testid="message-submit-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} {uploading ? "Uploading photos…" : "Send message"}
          </button>
        </div>
      </form>
    </div>
  );
};
